<template>
  <div class="candidate-sessions-page">
    <!-- Page Header -->
    <div class="page-header">
      <div class="breadcrumb-nav">
        <router-link :to="{ name: 'chatbot' }" class="breadcrumb-link">
          <i class="las la-home"></i>
          Financial Assistant
        </router-link>
        <i class="las la-chevron-right breadcrumb-separator"></i>
        <span class="breadcrumb-current">Chat Sessions</span>
      </div>
      <h1 class="page-title">Chat Sessions</h1>
      <p class="page-subtitle">Manage and organize your financial document conversations</p>
    </div>
    
    <!-- Main Content -->
    <div class="main-content">
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>Loading candidate information...</p>
      </div>
      <div v-else-if="error" class="error-state">
        <p>{{ error }}</p>
        <button @click="goBackToLanding" class="back-btn">Go Back</button>
      </div>
      <ChatSessionSelector 
        v-else
        :selected-candidate="selectedCandidate"
      />
    </div>
  </div>
</template>

<script>
import ChatSessionSelector from './components/ChatSessionSelector.vue'
import ChatbotService from "@/services/chatbot.service";

export default {
  name: 'CandidateSessions',
  components: {
    ChatSessionSelector
  },
  props: {
    candidateId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      selectedCandidate: {},
      loading: false,
      error: null
    }
  },
  async mounted() {
    await this.fetchCandidateInfo();
  },
  methods: {
    async fetchCandidateInfo() {
      this.loading = true;
      try {
        const candidates = await ChatbotService.getFinancingCandidates();
        const candidate = Array.isArray(candidates) 
          ? candidates.find(c => c.id === this.candidateId)
          : (candidates.candidates || candidates.data || []).find(c => c.id === this.candidateId);
        
        if (candidate) {
          this.selectedCandidate = candidate;
        } else {
          this.error = 'Candidate not found';
          this.goBackToLanding();
        }
      } catch (err) {
        this.error = 'Failed to load candidate information';
        console.error('Error fetching candidate:', err);
        this.goBackToLanding();
      } finally {
        this.loading = false;
      }
    },
    
    goBackToLanding() {
      this.$router.push({ name: 'chatbot' });
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../chatbot/index.scss';

.candidate-sessions-page {
  width: 100%;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.page-header {
  background: #ffffff;
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
}

.breadcrumb-nav {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.9rem;
  color: #6c757d;
}

.breadcrumb-link {
  color: $color-primary;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  
  &:hover {
    color: darken($color-primary, 10%);
    text-decoration: none;
  }
  
  i {
    font-size: 1rem;
  }
}

.breadcrumb-separator {
  color: #adb5bd;
  font-size: 0.8rem;
}

.breadcrumb-current {
  color: #495057;
  font-weight: 500;
}

.main-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  overflow: auto;
}

.loading-state, .error-state {
  text-align: center;
  padding: 3rem 2rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid $color-primary;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-state p {
  color: #718096;
  font-size: 1rem;
  margin: 0;
}

.error-state p {
  color: #e53e3e;
  font-size: 1rem;
  margin: 0 0 1rem 0;
}

.error-state .back-btn {
  background: $color-primary;
  color: $color-secondary;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: darken($color-primary, 10%);
    transform: translateY(-1px);
  }
}

// Responsive design
@media (max-width: 768px) {
  .page-header {
    padding: 1rem;
  }
  
  .main-content {
    padding: 1rem;
  }
  
  .breadcrumb-nav {
    font-size: 0.8rem;
    gap: 0.5rem;
  }
}
</style> 